/**
 * AWS Configuration for the Admin UI
 * This file contains configuration for AWS services including Cognito Authentication
 */

// Get the current domain and port for local development
let currentDomain = 'localhost:3000';
if (typeof window !== 'undefined') {
  currentDomain = window.location.hostname;
  if (window.location.port) {
    currentDomain = `${currentDomain}:${window.location.port}`;
  }
}

// Determine if we're in development or production environment
const isDevelopment = process.env.NODE_ENV === 'development';

// Ensure the required environment variables are defined
const missingVars: string[] = [];
const requiredVars = [
  'REACT_APP_COGNITO_USER_POOL_ID',
  'REACT_APP_COGNITO_CLIENT_ID',
  'REACT_APP_API_ENDPOINT',
  'REACT_APP_COGNITO_DOMAIN'
];

requiredVars.forEach(varName => {
  if (!process.env[varName]) {
    missingVars.push(varName);
    console.error(`Missing required environment variable: ${varName}`);
  }
});

if (missingVars.length > 0) {
  console.warn(`Missing configuration variables: ${missingVars.join(', ')}`);
}

// Configuration for Cognito and other AWS services
export const awsConfig = {
  // AWS Region for services
  region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
  
  // Auth configuration for Cognito
  Auth: {
    region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
    identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID,
    mandatorySignIn: true,
    oauth: {
      domain: process.env.REACT_APP_COGNITO_DOMAIN,
      scope: ['email', 'openid', 'profile'],
      redirectSignIn: process.env.REACT_APP_REDIRECT_SIGN_IN || `http://${currentDomain}/callback`,
      redirectSignOut: process.env.REACT_APP_REDIRECT_SIGN_OUT || `http://${currentDomain}/`,
      responseType: 'code'
    }
  },
  
  // API Gateway configuration
  API: {
    endpoints: [
      {
        name: 'CustomerFeedbackAPI',
        endpoint: process.env.REACT_APP_API_ENDPOINT || '/api/v1',
        region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
        custom_header: async () => {
          try {
            // Import Auth dynamically to avoid circular dependencies
            const { Auth } = await import('aws-amplify');
            const session = await Auth.currentSession();
            return {
              Authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
            };
          } catch (error) {
            console.error('Error getting auth token for API request:', error);
            return {};
          }
        }
      }
    ]
  },
  
  // Legacy cognito config (for backward compatibility)
  cognito: {
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
    identityPoolId: process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID,
    domain: process.env.REACT_APP_COGNITO_DOMAIN,
    redirectSignIn: process.env.REACT_APP_REDIRECT_SIGN_IN || `http://${currentDomain}/callback`,
    redirectSignOut: process.env.REACT_APP_REDIRECT_SIGN_OUT || `http://${currentDomain}/`,
    scope: ['email', 'openid', 'profile'],
    responseType: 'code'
  }
};

// Log configuration for debugging (redacting sensitive info)
console.log('AWS Config initialized:', {
  region: awsConfig.region,
  apiEndpoint: process.env.REACT_APP_API_ENDPOINT,
  cognitoDomain: process.env.REACT_APP_COGNITO_DOMAIN,
  environment: process.env.NODE_ENV
});

export default awsConfig;
